/* Risuono · scheda prodotto — barra d'acquisto fissa.
 * Il pulsante della barra attiva il pulsante reale di Dawn: variante, carrello e messaggi d'errore
 * restano gestiti da product-form.js. La barra si aggiorna leggendo l'input nascosto name="id"
 * (evento "change" emesso da product-info.js) e l'evento PUB_SUB_EVENTS.variantChange. */
(() => {
  if (customElements.get('risuono-sticky-atc')) return;

  const decode = (value) => {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value || '';
    return new DOMParser().parseFromString(value, 'text/html').documentElement.textContent;
  };

  class RisuonoStickyAtc extends HTMLElement {
    connectedCallback() {
      // Move the bar to <body> once, so no transformed ancestor can break position: fixed.
      if (this.parentElement !== document.body) {
        this.moving = true;
        document.body.appendChild(this);
        return;
      }
      this.moving = false;
      if (this.ready) return;
      this.ready = true;
      this.init();
    }

    disconnectedCallback() {
      if (this.moving) return;
      this.teardown();
    }

    init() {
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!this.form || !this.realButton) {
        this.remove();
        return;
      }

      try {
        this.variants = JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '{}');
      } catch (error) {
        this.variants = {};
      }

      this.button = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.variantEl = this.querySelector('[data-sticky-variant]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.imageEl = this.querySelector('[data-sticky-image]');
      this.state = { pastButton: false, footerVisible: false };
      this.visible = false;

      this.onClick = this.onClick.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.onSectionUnload = this.onSectionUnload.bind(this);
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('change', this.onInputChange);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);

      // Mirror the real button: disabled, loading and label ("Aggiungi al carrello", "Esaurito"...).
      this.buttonObserver = new MutationObserver(() => this.syncButton());
      this.buttonObserver.observe(this.realButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || String(data.sectionId) !== String(this.sectionId)) return;
          this.updateVariant(data.variant ? data.variant.id : '');
        });
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, () => {
          if (!this.visible) return;
          const wrapper = this.realButton.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
          (wrapper && !wrapper.hidden ? wrapper : this.realButton).scrollIntoView({
            block: 'center',
            behavior: this.reducedMotion() ? 'auto' : 'smooth',
          });
        });
      }

      if ('IntersectionObserver' in window) {
        this.footer =
          document.querySelector('.shopify-section-group-footer-group') ||
          document.querySelector('footer.footer') ||
          document.querySelector('footer');
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.realButton) {
              this.state.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
            } else {
              this.state.footerVisible = entry.isIntersecting;
            }
          });
          this.toggle();
        });
        this.observer.observe(this.realButton);
        if (this.footer) this.observer.observe(this.footer);
      }

      const input = this.form.querySelector('input[name="id"]');
      if (input && input.value) this.updateVariant(input.value);
      this.syncButton();
    }

    teardown() {
      this.observer?.disconnect();
      this.buttonObserver?.disconnect();
      this.unsubscribeVariant?.();
      this.unsubscribeError?.();
      document.removeEventListener('change', this.onInputChange);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      this.ready = false;
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onSectionUnload(event) {
      if (event.detail && String(event.detail.sectionId) === String(this.sectionId)) this.remove();
    }

    onInputChange(event) {
      const target = event.target;
      if (!target || target.name !== 'id' || target.form !== this.form) return;
      this.updateVariant(target.value);
    }

    onClick() {
      if (this.realButton.disabled || this.realButton.getAttribute('aria-disabled') === 'true') return;
      this.realButton.click();
    }

    updateVariant(id) {
      const variant = id ? this.variants[String(id)] : null;
      this.classList.toggle('is-unavailable', !variant);
      if (!variant) return;
      if (this.variantEl) this.variantEl.textContent = variant.title;
      if (this.priceEl) this.priceEl.textContent = decode(variant.price);
      if (this.imageEl && variant.image && this.imageEl.getAttribute('src') !== variant.image) {
        this.imageEl.setAttribute('src', variant.image);
      }
    }

    syncButton() {
      const real = this.realButton;
      const loading = real.classList.contains('loading');
      this.button.disabled = real.disabled;
      this.button.classList.toggle('is-loading', loading);
      if (loading) {
        this.button.setAttribute('aria-busy', 'true');
      } else {
        this.button.removeAttribute('aria-busy');
      }
      const labelSource = Array.from(real.querySelectorAll(':scope > span')).find(
        (span) => !span.classList.contains('hidden') && span.textContent.trim() !== ''
      );
      const text = labelSource ? labelSource.textContent.trim() : '';
      if (text && this.label.textContent.trim() !== text) this.label.textContent = text;
    }

    toggle() {
      const show = this.state.pastButton && !this.state.footerVisible;
      if (show === this.visible) return;
      this.visible = show;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      if (show) {
        this.removeAttribute('inert');
      } else {
        this.setAttribute('inert', '');
      }
    }
  }

  customElements.define('risuono-sticky-atc', RisuonoStickyAtc);
})();
